import React from "react";
import { useSnapshot } from "valtio";
import { ToastContainer } from "react-toastify";
import { useTranslation } from "react-i18next";
import { StyledBreadcrumb } from "@aureskonnect/react-ui";
import classnames from "classnames";
import { DynamicTable } from "@maherunlocker/custom-react-table";

import { store } from "@components/VerticalLayout/store";
import {
    store as localStore,
    setActions,
    setIsActionsClicked,
    setIsEdited,
    setKey,
    setValidationAction,
} from "./store";

import PageTitle from "@components/Common/PageTitle";
import { LocationForm } from "./LocationForm";
import MessageInfo from "@components/Common/MessageInfo";
import { CustomMainColorButtonWithIcon } from "@components/Common/CustomMainColorButtonWithIcon";
import { ConfirmationUpdatingDataMessageModal } from "@components/Common/ConfirmationUpdatingDataMessageModal";
import { useCallbackPrompt } from "@hooks/useCallbackPrompt";
import { disableCheckbox } from "./helpers";
import { ArrayOfCustomColumns } from "./ArrayOfCustomColumns";
import { ConfirmationDeleteLocation } from "./ConfirmationDeleteLocation";
import { useSelector } from "react-redux";

export function LocationManagement() {
    const { oneShop, userID, shopID, franchiseID } = useSnapshot(store);
    const {
        key,
        isActionsClicked,
        validationAction,
        actions,
        isEdited,
    } = useSnapshot(localStore);
    const { t } = useTranslation();
    const uuidUser = localStorage.getItem("uuidUser");
    const selectedAccount = useSelector(
        (state: any) => state.global.selectedAccount
    );
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const qrCodeRefs = React.useRef<Record<string, HTMLDivElement | null>>({});
    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(
        isEdited
    );

    const [isAddLocationClicked, setIsAddLocationClicked] = React.useState<
        boolean
    >(false);
    const [filterActive, setLocalFilterActive] = React.useState<boolean>(false);
    const [rowData, setRowData] = React.useState<any>({});
    const [selectedRows, setSelectedRows] = React.useState<any>([]);
    const [isConsult, setIsConsult] = React.useState<boolean>(false);
    const [isValidateDeleteOpened, setIsValidateDeleteOpened] = React.useState<
        boolean
    >(false);

    let arrayOfCustomColumns = ArrayOfCustomColumns({
        qrCodeRefs,
        setRowData,
        setIsAddLocationClicked,
        setLocalFilterActive,
        setIsValidateDeleteOpened,
    });

    React.useEffect(() => {
        if (isAddLocationClicked === true && selectedRows.length > 0) {
            disableCheckbox();
        }
    }, [isAddLocationClicked, selectedRows]);
    React.useEffect(() => {
        setActions(confirmNavigation);
        setIsActionsClicked(showPrompt as boolean);
        setValidationAction(cancelNavigation);
    }, [showPrompt, confirmNavigation, cancelNavigation]);
    React.useEffect(() => {
        setKey(Math.floor(Math.random() * 10000));
    }, [i18nextLng]);
    return (
        <React.Fragment>
            <ToastContainer />
            <PageTitle title={t("Location management")} />
            <div className="page-content__clz ">
                <div className="mdi_txt_informationModes">
                    <StyledBreadcrumb
                        items={[
                            {
                                item: "Settings".toUpperCase(),
                                link: `${process.env.REACT_APP_PORTAIL_URL}`,
                            },
                            {
                                item: selectedAccount?.toUpperCase() || "",
                                link: oneShop
                                    ? "/setting/customer-account"
                                    : "/compte",
                            },
                            {
                                item: t("General settings").toUpperCase(),
                                link: "#",
                            },
                            {
                                item: t("Location management").toUpperCase(),
                                link: "#",
                            },
                        ]}
                    />
                </div>
                <MessageInfo
                    message={t(
                        "When configuring the location QR code, if you select a sales mode, it will be enforced for the customer. If you leave it blank, the customer will be able to choose their preferred sales mode when scanning."
                    )}
                />
                <div
                    style={{
                        display: "grid",
                        minHeight: oneShop ? "650px" : "600px",
                        gridTemplateColumns: isAddLocationClicked
                            ? "2fr 1fr "
                            : "auto",
                        gridColumnGap: "4px",
                        border: 0,
                    }}
                >
                    <DynamicTable
                        canSort
                        key={key}
                        requestHeader={{ "Accept-Language": i18nextLng }}
                        url={
                            oneShop
                                ? `${process.env.REACT_APP_API_V2_URL}/settings/general/locationManagement?userId=${userID}&shopId=${shopID}&franchiseId=${franchiseID}&uuidUser=${uuidUser}`
                                : `${process.env.REACT_APP_API_V2_URL}/settings/general/locationManagement?userId=${userID}&franchiseId=${franchiseID}&uuidUser=${uuidUser}`
                        }
                        canExpand={!oneShop}
                        canMovedCheckboxLeftOnExpand
                        canSelect={!oneShop}
                        customSelect
                        setSelectedRows={setSelectedRows}
                        customJsxSideFilterButton={
                            isAddLocationClicked !== true ? (
                                <span
                                    onClick={() => {
                                        disableCheckbox();
                                        setRowData({});
                                        setIsAddLocationClicked(true);
                                        setLocalFilterActive(false);
                                    }}
                                    className={`${classnames(
                                        "w-100 cmn_btn_openForm ml-2",
                                        {
                                            "not-allowed-icon__clz":
                                                (selectedRows.length === 0 ||
                                                    selectedRows.length > 1) &&
                                                !oneShop,
                                        }
                                    )}`}
                                >
                                    <CustomMainColorButtonWithIcon
                                        icon="PlusIcon"
                                        iconPosition="left"
                                        rounded
                                        variant="primary"
                                        style={{ marginLeft: "5px !important" }}
                                    >
                                        {t("Add a location")}
                                    </CustomMainColorButtonWithIcon>
                                </span>
                            ) : null
                        }
                        canResize
                        showGlobalFilter
                        showFilter
                        setLocalFilterActive={setLocalFilterActive}
                        filterActive={filterActive}
                        actionColumn={() => <></>}
                        arrayOfCustomColumns={arrayOfCustomColumns}
                        elevationTable={1}
                        minHeight={oneShop ? "500px" : "450px"}
                        maxHeight={oneShop ? "500px" : "450px"}
                        name="locationManagement"
                    />
                    {isAddLocationClicked ? (
                        <LocationForm
                            setIsAddLocationClicked={setIsAddLocationClicked}
                            isConsult={isConsult}
                            setIsConsult={setIsConsult}
                            shopId={
                                oneShop
                                    ? shopID
                                    : rowData.shopId !== undefined
                                    ? rowData.shopId
                                    : selectedRows[0] !== undefined
                                    ? selectedRows[0].shopId
                                    : undefined
                            }
                            qrCodeRefs={qrCodeRefs}
                            editedData={rowData}
                        />
                    ) : null}
                </div>
                {isActionsClicked === true ? (
                    <ConfirmationUpdatingDataMessageModal
                        isModalOpened={isActionsClicked}
                        setIsModalOpened={setIsActionsClicked}
                        validationAction={validationAction}
                        actions={actions}
                        setIsEdited={setIsEdited}
                    />
                ) : null}
            </div>
            {isValidateDeleteOpened ? (
                <ConfirmationDeleteLocation
                    rowData={rowData}
                    isOpened={isValidateDeleteOpened}
                    setIsOpened={setIsValidateDeleteOpened}
                />
            ) : null}
        </React.Fragment>
    );
}
